import { Fragment, useCallback, useEffect, useRef, useState, type ComponentType, type SVGProps } from 'react';
import { useClient } from '../lib/ClientContext';
import { logout } from '../lib/session';
import { Mailbox } from './Mailbox';
import { CalendarScreen } from './CalendarScreen';
import { ContactsScreen } from './ContactsScreen';
import { TasksScreen } from './TasksScreen';
import { SettingsScreen } from './SettingsScreen';
import { AskAI } from './AskAI';
import {
  IconAI,
  IconBriefcase,
  IconCalendar,
  IconContacts,
  IconMail,
  IconSearch,
  IconSettings,
  IconShare,
  IconTasks,
  IconBroom,
} from './icons';
import { CleanPanel, CleanProvider, useCleanRunning } from './CleanPanel';

type AppKey = 'mail' | 'calendar' | 'contacts' | 'tasks' | 'briefcase' | 'settings';

const APPS: Array<{ key: AppKey; label: string; Icon: ComponentType<SVGProps<SVGSVGElement>> }> = [
  { key: 'mail', label: 'Mail', Icon: IconMail },
  { key: 'calendar', label: 'Calendar', Icon: IconCalendar },
  { key: 'contacts', label: 'Contacts', Icon: IconContacts },
  { key: 'tasks', label: 'Tasks', Icon: IconTasks },
  { key: 'briefcase', label: 'Briefcase', Icon: IconBriefcase },
  { key: 'settings', label: 'Settings', Icon: IconSettings },
];

const LS_CLEAN_PINNED = 'clean.pinned';
function lsGet(k: string): string | null {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
function lsSet(k: string, v: string) {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* private mode — not remembered */
  }
}

/** Top-bar broom; sweeps while a clean is running. */
function CleanAppButton({ onClick }: { onClick: () => void }) {
  const running = useCleanRunning();
  return (
    <button
      type="button"
      className={`appicon clean-app${running ? ' running' : ''}`}
      title={running ? 'Cleaning your inbox…' : 'Clean inbox'}
      aria-label="Clean inbox"
      onClick={onClick}
    >
      <IconBroom />
    </button>
  );
}

export function AppShell({ onLogout }: { onLogout: () => void }) {
  const { session } = useClient();
  const [app, setApp] = useState<AppKey>('mail');
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [aiOpen, setAiOpen] = useState(false);
  // Clean inbox panel — a pinned panel reopens after reload
  const [cleanPinned, setCleanPinned] = useState(() => lsGet(LS_CLEAN_PINNED) === '1');
  const [cleanOpen, setCleanOpen] = useState(cleanPinned);
  const openClean = useCallback(() => setCleanOpen(true), []);
  const [openMsgId, setOpenMsgId] = useState<string | null>(null);
  const [newVer, setNewVer] = useState<string | null>(null);
  const baseVer = useRef<string | null>(null);
  const current = APPS.find((a) => a.key === app);

  // poll the deployed build id; when it changes, offer to update (decline → it
  // applies on the next reload anyway, since index.html is served no-cache).
  useEffect(() => {
    const check = async () => {
      try {
        const r = await fetch('/api/version', { cache: 'no-store' });
        if (!r.ok) return;
        const { ver } = (await r.json()) as { ver?: string };
        if (!ver) return;
        if (baseVer.current === null) baseVer.current = ver;
        else if (ver !== baseVer.current) setNewVer(ver);
      } catch {
        /* ignore */
      }
    };
    void check();
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
  }, []);

  return (
    <CleanProvider onOpenPanel={openClean}>
    <div className="app">
      <header className="topbar">
        <div className="logo">
          <span className="logo-mark">M</span>
          <span className="logo-text">Mo2&nbsp;Mail</span>
        </div>

        <nav className="apps">
          {APPS.map(({ key, label, Icon }) => (
            <Fragment key={key}>
              {key === 'settings' && session.shareEnabled ? (
                <a
                  className="appicon share-app"
                  href="/api/share/open"
                  target="_blank"
                  rel="noopener"
                  title="Deling — del filer sikkert"
                  aria-label="Deling — del filer sikkert"
                >
                  <IconShare />
                </a>
              ) : null}
              {key === 'settings' ? <CleanAppButton onClick={() => setCleanOpen((v) => !v)} /> : null}
              {key === 'settings' ? (
                <button
                  type="button"
                  className="appicon ai-app"
                  title="Ask your mailbox (AI)"
                  aria-label="Ask your mailbox"
                  onClick={() => setAiOpen(true)}
                >
                  <IconAI />
                </button>
              ) : null}
              <button
                className={`appicon${app === key ? ' active' : ''}`}
                title={label}
                aria-label={label}
                aria-current={app === key}
                onClick={() => setApp(key)}
              >
                <Icon />
              </button>
            </Fragment>
          ))}
        </nav>

        <form
          className="search"
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(term.trim());
            setApp('mail');
          }}
        >
          <IconSearch />
          <input
            type="text"
            placeholder="Search mail…"
            value={term}
            onChange={(e) => {
              setTerm(e.target.value);
              if (e.target.value === '') setQuery('');
            }}
          />
          {term ? (
            <button
              type="button"
              className="search-clear"
              title="Clear search"
              aria-label="Clear search"
              onClick={() => {
                setTerm('');
                setQuery('');
              }}
            >
              ✕
            </button>
          ) : null}
        </form>

        <div className="account">
          <span className="who">{session.user}</span>
          <button
            className="ghost"
            onClick={async () => {
              await logout();
              onLogout();
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="workspace">
        {app === 'mail' ? (
          <Mailbox
            query={query}
            session={session.user}
            openMsgId={openMsgId}
            onSearch={(q) => {
              setTerm(q);
              setQuery(q);
            }}
          />
        ) : app === 'calendar' ? (
          <CalendarScreen />
        ) : app === 'contacts' ? (
          <ContactsScreen />
        ) : app === 'tasks' ? (
          <TasksScreen />
        ) : app === 'settings' ? (
          <SettingsScreen />
        ) : (
          <div className="placeholder">
            {current ? <current.Icon className="placeholder-icon" /> : null}
            <h2>{current?.label}</h2>
            <p>Coming soon — the SDK already covers it; the screen is next.</p>
          </div>
        )}
      </div>

      {aiOpen ? (
        <AskAI
          onClose={() => setAiOpen(false)}
          onOpenMessage={(id) => {
            setApp('mail');
            setOpenMsgId(id);
          }}
        />
      ) : null}

      {cleanOpen ? (
        <CleanPanel
          pinned={cleanPinned}
          onPin={(v) => {
            setCleanPinned(v);
            lsSet(LS_CLEAN_PINNED, v ? '1' : '0');
          }}
          onClose={() => {
            setCleanOpen(false);
            setCleanPinned(false);
            lsSet(LS_CLEAN_PINNED, '0');
          }}
        />
      ) : null}

      {newVer ? (
        <div className="update-toast">
          <div className="update-text">
            <strong>Update available</strong>
            <span>A new version of Mo2 Mail is ready.</span>
          </div>
          <button className="ghost" onClick={() => { baseVer.current = newVer; setNewVer(null); }}>
            Later
          </button>
          <button className="primary" onClick={() => window.location.reload()}>
            Update now
          </button>
        </div>
      ) : null}
    </div>
    </CleanProvider>
  );
}
